import { z } from 'zod';

export type ValidationError =
  | { type: 'EMPTY' }
  | { type: 'TOO_LONG'; maxLength: number }
  | { type: 'TOO_SHORT'; minLength: number }
  | { type: 'INVALID_CHARACTERS'; characters: string[] }
  | { type: 'TOO_YOUNG'; minAge: number }
  | { type: 'TOO_OLD'; maxAge: number }
  | { type: 'INVALID_EMAIL' };

export type ValidationResult =
  | { isValid: true }
  | { isValid: false; error: ValidationError };

/**
 * @param value The input string to validate.
 * @param regex The regex pattern that defines the allowed characters.
 * @param options Options to customize the validation. By default, we check if against a regex that allows the characters.
 * If you want to check for invalid characters, you can set this option to false.
 *
 * @returns A set of invalid characters.
 *
 * Utility function to extract invalid characters from a string.
 */
export const extractInvalidCharacters = (
  value: string,
  regex: RegExp,
  options: { shouldCheckIfValueIsAllowed?: boolean } = {
    shouldCheckIfValueIsAllowed: true,
  },
): Set<string> => {
  const { shouldCheckIfValueIsAllowed } = options;

  return new Set(
    value
      .split('')
      .filter(char =>
        shouldCheckIfValueIsAllowed ? !regex.test(char) : regex.test(char),
      ),
  );
};

/**
 * Validates that the option selected is valid.
 * @param title Option selected.
 * @returns True if valid, false if not.
 */
export const isSelectOptionValid = (option: string) => {
  return option !== 'default';
};

/**
 * Validates a generic text field.
 * @param value Text field value.
 * @returns True if valid, false if not.
 */
export const isTextValid = (value: string): boolean => {
  if (value.length === 0) return false;

  // The value should not consist in only spaces.
  const valueWithoutSpaces = value.replaceAll(' ', '');
  return valueWithoutSpaces.length > 0;
};

export const isCityValid = (value: string): ValidationResult => {
  if (!isTextValid(value)) {
    return { isValid: false, error: { type: 'EMPTY' } };
  }

  if (value.length > 100) {
    return { isValid: false, error: { type: 'TOO_LONG', maxLength: 100 } };
  }

  const allowedCharacters = /^[a-zA-Z\-\s]+$/;
  if (!allowedCharacters.test(value)) {
    const invalidCharacters = extractInvalidCharacters(
      value,
      allowedCharacters,
    );
    return {
      isValid: false,
      error: {
        type: 'INVALID_CHARACTERS',
        characters: Array.from(invalidCharacters),
      },
    };
  }

  return { isValid: true };
};

export const isNameValid = (value: string): ValidationResult => {
  if (!isTextValid(value)) {
    return { isValid: false, error: { type: 'EMPTY' } };
  }

  if (value.length > 100) {
    return { isValid: false, error: { type: 'TOO_LONG', maxLength: 100 } };
  }

  const invalidCharacters =
    /[!@#$^&*(),.?":{}|<>[\]`~+=_;%£€¥§±•∑®†¡¢∞¶ªº–≠'πæ«÷≥≤µ∫√ç≈ƒ©˙∆˚¬…¨\\/]/;

  if (invalidCharacters.test(value)) {
    const allowedCharacters = /^[a-zA-Z0-9\s_\-&.]+$/;
    const invalidChars = extractInvalidCharacters(value, allowedCharacters);
    return {
      isValid: false,
      error: {
        type: 'INVALID_CHARACTERS',
        characters: Array.from(invalidChars),
      },
    };
  }

  return { isValid: true };
};

/**
 * Validates a date of birth input.
 * @param input Date of birth selected.
 * @returns True if valid, false if not.
 */
export const isAgeValid = (input: Date): ValidationResult => {
  const today = new Date();

  const birthDate = new Date(input);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();

  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  if (age < 18) {
    return {
      isValid: false,
      error: { type: 'TOO_YOUNG', minAge: 18 },
    };
  } else if (age > 120) {
    return {
      isValid: false,
      error: { type: 'TOO_OLD', maxAge: 120 },
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Validates a street address field.
 * @param value Street address field value.
 * @returns True if valid, false if not, along with invalid characters if any.
 */
export const isStreetAddressValid = (value: string): ValidationResult => {
  if (!isTextValid(value)) {
    return { isValid: false, error: { type: 'EMPTY' } };
  }

  if (value.length > 200) {
    return { isValid: false, error: { type: 'TOO_LONG', maxLength: 200 } };
  }

  const allowedCharacters = /^[a-zA-Z0-9\s\-/\\#'!.,]+$/;
  if (!allowedCharacters.test(value)) {
    const invalidCharacters = extractInvalidCharacters(
      value,
      allowedCharacters,
    );
    return {
      isValid: false,
      error: {
        type: 'INVALID_CHARACTERS',
        characters: Array.from(invalidCharacters),
      },
    };
  }

  return { isValid: true };
};

/**
 * Validates a postcode field.
 * @param value Postcode field value.
 * @returns True if valid, false if not, along with invalid characters if any.
 */
export const isPostcodeValid = (value: string): ValidationResult => {
  if (!isTextValid(value)) {
    return { isValid: false, error: { type: 'EMPTY' } };
  }

  if (value.length > 15) {
    return { isValid: false, error: { type: 'TOO_LONG', maxLength: 15 } };
  }

  const allowedCharacters = /^[a-zA-Z0-9\s]+$/;
  if (!allowedCharacters.test(value)) {
    const invalidCharacters = extractInvalidCharacters(
      value,
      allowedCharacters,
    );
    return {
      isValid: false,
      error: {
        type: 'INVALID_CHARACTERS',
        characters: Array.from(invalidCharacters),
      },
    };
  }

  return { isValid: true };
};

/**
 * Validates that an email has an appropriate format.
 * @param email Email field value
 * @returns True if valid, false if not.
 */
export const isValidEmail = (email: string): ValidationResult => {
  const emailSchema = z.email({ pattern: z.regexes.rfc5322Email }).max(320);
  const result = emailSchema.safeParse(email);

  return result.success
    ? { isValid: true }
    : { isValid: false, error: { type: 'INVALID_EMAIL' } };
};

/**
 * Validates that a confirmation email is valid.
 * @param email Email field value
 * @param confirmationEmail Confirmation email field value
 * @returns True if valid, false if not.
 */
export const isConfirmationEmailValid = (
  email: string,
  confirmationEmail: string,
) => {
  return email === confirmationEmail;
};

/**
 * Validates that password has minimum 8 characters.
 * @param password Password field value.
 * @returns True if valid, false if not.
 */
export const hasMinimum8Characters = (password: string): boolean => {
  return password.length >= 8;
};

/**
 * Validates that password has maximum 20 characters.
 * @param password Password field value.
 * @returns True if valid, false if not.
 */
export const hasMaximum20Characters = (password: string): boolean => {
  return password.length <= 20;
};

/**
 * Validates that password contains at least one lowercase letter.
 * @param password Password field value.
 * @returns True if valid, false if not.
 */
export const hasLowerCaseLetter = (password: string): boolean => {
  return /[a-z]/.test(password);
};

/**
 * Validates that password contains at least one uppercase letter.
 * @param password Password field value.
 * @returns True if valid, false if not.
 */
export const hasUpperCaseLetter = (password: string): boolean => {
  return /[A-Z]/.test(password);
};

/**
 * Validates that password contains at least one number.
 * @param password Password field value.
 * @returns True if valid, false if not.
 */
export const hasNumber = (password: string): boolean => {
  return /\d/.test(password);
};

/**
 * Validates that password contains at least one allowed symbol.
 * @param password Password field value.
 * @returns True if valid, false if not.
 */
export const hasSymbol = (password: string): boolean => {
  /**
   * We are only allowing the following special characters:
   * ~!"#$'\\*+,\-./:;<>?@[\]^_`{|}
   * We need at least one of these special characters in the password.
   */
  const allowedPasswordCharacters =
    /^(?=.*[~!"#$'\\*+,\-./:;<>?@[\]^_`{|}])[a-zA-Z0-9~!"#$'\\*+,\-./:;<>?@[\]^_`{|}]*$/;

  return allowedPasswordCharacters.test(password);
};

/**
 * Validates that a password is valid.
 * @param password Password field value.
 * @returns True if valid, false if not.
 */
export const isValidPassword = (password: string): boolean => {
  return (
    hasMinimum8Characters(password) &&
    hasMaximum20Characters(password) &&
    hasLowerCaseLetter(password) &&
    hasUpperCaseLetter(password) &&
    hasNumber(password) &&
    hasSymbol(password)
  );
};

/**
 * Validates if the user accepted the terms and conditions.
 * @param value Checkbox value for the terms and conditions.
 * @returns True if valid, false if not.
 */
export const isValidTermsAndConditions = (value: boolean) => value === true;
